import { ComponentProps, ComponentType } from "react";
import { theme } from "@theme";
import { secondsToTime } from "@utils/index";
import { TextStyle, TextInput } from "react-native";
import Animated, {
  useAnimatedProps,
  SharedValue,
} from "react-native-reanimated";

type AnimatedTimeProps = Partial<ComponentProps<typeof TextInput>> & {
  text?: string;
};
type AnimatedTextInputProps = ComponentProps<typeof TextInput> & {
  animatedProps?: AnimatedTimeProps;
};

const AnimatedTextInput = Animated.createAnimatedComponent(
  TextInput,
) as ComponentType<AnimatedTextInputProps>;
export const secondsToTimeWorklet = (secs: number) => {
  "worklet";
  const totalSeconds = Math.max(0, Math.floor(secs));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const hString = `${hours}:`;
  const mString = hours > 0 && minutes < 10 ? `0${minutes}:` : `${minutes}:`;
  const sString = seconds < 10 ? `0${seconds}` : `${seconds}`;
  if (hours > 0) {
    return hString + mString + sString;
  }
  return mString + sString;
};
const variant = theme.textVariants["Caption/C1/Regular"];
const commonStyle: TextStyle = {
  width: "100%",
  fontSize: variant.fontSize,
  fontFamily: variant.fontFamily,
  letterSpacing: variant.letterSpacing,
  color: theme.colors.textPrimary,
  opacity: 0.6,
  padding: 0,
  margin: 0,
  includeFontPadding: false,
  textAlignVertical: "center",
  textAlign: "center",
};
/**
 * High-performance time label that updates via UI thread properties.
 * Use this for the current progress position.
 */
const AnimatedTimeLabel = ({ value }: { value: SharedValue<number> }) => {
  const animatedProps = useAnimatedProps<AnimatedTimeProps>(() => {
    const formattedTime = secondsToTimeWorklet(value.value);
    return {
      text: formattedTime,
      defaultValue: formattedTime,
    };
  });
  return (
    <AnimatedTextInput
      allowFontScaling={false}
      maxFontSizeMultiplier={1}
      underlineColorAndroid="transparent"
      editable={false}
      multiline={false}
      scrollEnabled={false}
      pointerEvents="none"
      // Keep this uncontrolled: a React value commit can overwrite Reanimated's
      // native text update and leave the elapsed counter frozen on iOS/Fabric.
      style={commonStyle}
      animatedProps={animatedProps}
    />
  );
};
/**
 * Static time label for values that don't need high-frequency updates.
 * Use this for song duration.
 */
const StaticTimeLabel = ({ value }: { value: number }) => {
  return (
    <TextInput
      allowFontScaling={false}
      maxFontSizeMultiplier={1}
      underlineColorAndroid="transparent"
      editable={false}
      multiline={false}
      scrollEnabled={false}
      pointerEvents="none"
      value={secondsToTime(Math.round(value))}
      style={commonStyle}
    />
  );
};

export const TimeLabel = ({
  positionValue,
}: {
  positionValue: SharedValue<number> | number;
}) => {
  if (typeof positionValue === "number") {
    return <StaticTimeLabel value={positionValue} />;
  }
  return <AnimatedTimeLabel value={positionValue} />;
};
